import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  changePassword,
  confirmPasswordReset,
  loadSession,
  requestPasswordReset,
  resendEmailCode,
  saveSession,
  signInWithEmail,
  signOutSession,
  signUpWithEmail,
  verifyEmailCode,
} from "@/services/authService";

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedSession = loadSession();
    setSession(storedSession);
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const nextSession = await signInWithEmail(email, password);
    setSession(nextSession);
    return nextSession;
  };

  const signup = async ({ name, email, password }) => {
    return signUpWithEmail({ name, email, password });
  };

  const verifyEmail = async ({ email, code }) => {
    const nextSession = await verifyEmailCode({ email, code });
    setSession(nextSession);
    return nextSession;
  };

  const sendVerificationEmail = async (email) => {
    return resendEmailCode(email);
  };

  const resetPassword = async (email) => {
    return requestPasswordReset(email);
  };

  const completePasswordReset = async ({ email, code, newPassword }) => {
    const nextSession = await confirmPasswordReset({ email, code, newPassword });
    setSession(nextSession);
    return nextSession;
  };

  const updatePassword = async ({ email, currentPassword, newPassword }) => {
    const response = await changePassword({ email, currentPassword, newPassword });
    // Password changes revoke all sessions; the server hands back a new one.
    if (response?.token && response?.user) {
      setSession({ token: response.token, user: response.user });
    }
    return response;
  };

  const logout = async () => {
    setSession(null);
    await signOutSession();
  };

  const refreshSession = (nextSession) => {
    const candidate = {
      token: nextSession?.token || session?.token,
      user: nextSession?.user || session?.user,
    };
    const stored = saveSession(candidate);
    setSession(stored);
    return stored;
  };

  const value = useMemo(
    () => ({
      currentUser: session?.user ?? null,
      token: session?.token ?? null,
      isAuthenticated: Boolean(session?.user),
      loading,
      login,
      logout,
      signup,
      verifyEmail,
      sendVerificationEmail,
      resetPassword,
      completePasswordReset,
      updatePassword,
      refreshSession,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, session]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
